import React, { useState } from 'react';
import { Phone, Mail, Send, CheckCircle, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { contactData } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Kérjük, add meg a neved';
    if (!formData.email.trim()) {
      newErrors.email = 'Kérjük, add meg az email címed';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Érvénytelen email cím';
    }
    if (!formData.message.trim()) newErrors.message = 'Kérjük, írd le az üzeneted';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    setSubmitError('');
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      if (response.data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      setSubmitError(
        error.response?.data?.detail ||
          'Hiba történt az üzenet küldésekor. Kérjük, próbálja újra később.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (submitError) setSubmitError('');
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const inputBase =
    'w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500 transition-all duration-300';

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-36 overflow-hidden"
      style={{ backgroundColor: '#0b1120' }}
    >
      {/* Orbs */}
      <div className="absolute top-20 left-10 w-80 h-80 bg-violet-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-fuchsia-500/15 rounded-full blur-3xl" />
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-14 lg:mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-fuchsia-300 mb-5">
            Kapcsolat
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold text-white leading-[1.05] tracking-tight text-balance" data-testid="contact-title">
            Kezdjük el a projektet
            <span className="block bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg,#a78bfa,#e879f9)' }}>
              közösen.
            </span>
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl">
            Foglalj egy kötelezettségmentes konzultációt, vagy írj nekünk közvetlenül.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            <a
              href={`tel:${contactData.phone.replace(/\s/g, '')}`}
              data-testid="contact-phone-link"
              className="group glass rounded-2xl p-6 flex items-center gap-4 hover:border-violet-500/40 transition-all duration-400 hover:-translate-y-0.5"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Telefon</div>
                <div className="text-lg text-white font-semibold group-hover:text-fuchsia-200 transition-colors">{contactData.phone}</div>
              </div>
            </a>

            <a
              href={`mailto:${contactData.email}`}
              data-testid="contact-email-link"
              className="group glass rounded-2xl p-6 flex items-center gap-4 hover:border-fuchsia-500/40 transition-all duration-400 hover:-translate-y-0.5"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Email</div>
                <div className="text-lg text-white font-semibold group-hover:text-fuchsia-200 transition-colors">{contactData.email}</div>
              </div>
            </a>

            <div className="mt-4 relative rounded-2xl p-[1px] bg-gradient-to-br from-violet-500/40 via-fuchsia-500/30 to-sky-500/30">
              <div className="relative rounded-[calc(1rem-1px)] bg-slate-900/80 backdrop-blur-md p-6 lg:p-7 overflow-hidden">
                <div className="grain-overlay" />
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-fuchsia-300" />
                  <span className="text-xs font-bold uppercase tracking-widest text-fuchsia-200">Ingyenes konzultáció</span>
                </div>
                <h4 className="font-display text-xl font-semibold text-white mb-2">30 perc, ami eldönti a projekt sikerét</h4>
                <p className="text-slate-300/90 text-sm leading-relaxed">
                  Foglalj egy kötelezettségmentes egyeztetést – átbeszéljük a céljaidat, a célközönséget és a legjobb utat az eredményekhez.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-[2rem] p-[1px] bg-gradient-to-br from-white/20 via-violet-400/30 to-fuchsia-500/30">
              <div className="relative rounded-[calc(2rem-1px)] bg-slate-50 p-7 sm:p-9 lg:p-10 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />

                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight mb-2">Írj nekünk</h3>
                <p className="text-slate-500 mb-7">Általában 24 órán belül válaszolunk.</p>

                {isSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-10" data-testid="contact-success">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-display text-xl font-semibold text-slate-900 mb-2">Köszönjük!</h4>
                    <p className="text-slate-500 text-center mb-6 max-w-sm">
                      Megkaptuk az üzeneted – 24 órán belül felvesszük veled a kapcsolatot.
                    </p>
                    <button
                      onClick={resetForm}
                      data-testid="contact-reset-button"
                      className="text-slate-500 hover:text-slate-800 transition-colors duration-300 text-sm underline underline-offset-4"
                    >
                      Új üzenet küldése
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="contact-form">
                    {submitError && (
                      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="contact-error">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-red-600 text-sm">{submitError}</p>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Név *</label>
                        <input
                          type="text" name="name" value={formData.name} onChange={handleChange}
                          data-testid="contact-name-input"
                          className={`${inputBase} ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
                          placeholder="Teljes név"
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Email *</label>
                        <input
                          type="email" name="email" value={formData.email} onChange={handleChange}
                          data-testid="contact-email-input"
                          className={`${inputBase} ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                          placeholder="email@pelda.hu"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Telefonszám</label>
                      <input
                        type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        data-testid="contact-phone-input"
                        className={`${inputBase} border-slate-200`}
                        placeholder="+36 ..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Üzenet *</label>
                      <textarea
                        name="message" value={formData.message} onChange={handleChange} rows={5}
                        data-testid="contact-message-input"
                        className={`${inputBase} resize-none ${errors.message ? 'border-red-400' : 'border-slate-200'}`}
                        placeholder="Miben segíthetünk?"
                      />
                      {errors.message && <p className="text-red-500 text-xs mt-1.5">{errors.message}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="shine group w-full inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-4 font-semibold text-white
                        bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-500 hover:to-fuchsia-400
                        shadow-[0_14px_40px_-14px_rgba(217,70,239,0.55)] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-0.5"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Küldés...
                        </>
                      ) : (
                        <>
                          Üzenet küldése
                          <Send className="w-4.5 h-4.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
